// components/admin/ContactMembers.jsx
import { useEffect, useState } from "react";
import { getAllCompanies } from "../../services/companiesService";
import { getContactsByCompany, deleteContact } from "../../services/contactsService";
import { useAuth } from "../../context/authContext";
import ContactDetailsModal from "./ContactDetailsModal";
import { Plus, Trash2 } from "lucide-react";
import Swal from "sweetalert2";
import { PageLoader, ActionLoader } from "../loader/index" 
import { Banner } from "../common/Banner";
import  ModalTransition  from "../common/ModalTransition";

const ContactMembers = ({ adminMode = true }) => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modalMode, setModalMode] = useState("view"); // "view" o "edit"
  const [isSubmitting, setIsSubmitting] = useState(false); // Estado para el loader de acción
  
  // Estados para el input de búsqueda
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  
  // Determinar si estamos en el modo "Mis Contactos" 
  const isMyContactsMode = !adminMode || user?.role !== "admin";
  
  const loadCompanies = async () => {
    try {
      const response = await getAllCompanies(user);
      // Filtrar empresas admin y la empresa del admin actual
      const filteredCompanies = user?.role === "admin" 
        ? response.filter(company => 
            company.business_name !== user.business_name && 
            company.role !== "admin"
          )
        : response;
      setCompanies(filteredCompanies);
    } catch (error) {
      console.error("Error loading companies:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar las empresas"
      });
    }
  };

  const loadContacts = async (businessName) => {
    try {
      setLoading(true);
      const response = await getContactsByCompany(businessName, user);
      setContacts(response.data || []);
    } catch (error) {
      console.error("Error loading contacts:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar los contactos"
      });
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      
      if (isMyContactsMode) {
        // Modo "Mis Contactos" - siempre carga los contactos del usuario actual
        setSelectedCompany(user.business_name);
        setSearchTerm(user.business_name); // Establecer el término de búsqueda
        await loadContacts(user.business_name);
      } else if (user?.role === "admin") {
        // Modo "Contactos de socios" para admin - carga la lista de empresas
        await loadCompanies();
        // Reseteamos la selección para evitar que quede preseleccionada la empresa del admin
        setSelectedCompany("");
        setSearchTerm("");
      }
      
      setLoading(false);
    };

    initializeData();
  }, [user, isMyContactsMode]);

  useEffect(() => {
    if (selectedCompany) {
      loadContacts(selectedCompany);
    }
  }, [selectedCompany]);

  // Función para filtrar compañías por múltiples campos
  const filterCompanies = (searchTerm) => {
    return companies.filter(company =>
      company.business_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.trade_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.rut?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Función para manejar la selección de compañía
  const handleCompanySelect = (company) => {
    setSelectedCompany(company.business_name);
    setSearchTerm(company.business_name);
    setShowDropdown(false);
    handleCompanyChange(company.business_name);
  };

  // Función para limpiar la selección
  const clearSelection = () => {
    setSelectedCompany("");
    setSearchTerm("");
    setContacts([]);
    handleCompanyChange("");
  };

  const handleCompanyChange = (businessName) => {
    setSelectedCompany(businessName);
  };

  const handleOpenModal = (contact = null, mode = "view") => {
    setSelectedContact(contact);
    setModalMode(mode);
    setIsModalOpen(true);
  };

  const handleCloseModal = (shouldRefresh = false) => {
    setSelectedContact(null);
    setIsModalOpen(false);
    
    // Recargar contactos si se realizó alguna operación
    if (shouldRefresh && selectedCompany) {
      loadContacts(selectedCompany);
    }
  };

  const handleDelete = async (contact) => {
    try {
      const result = await Swal.fire({
        title: "¿Eliminar contacto?",
        text: `¿Deseas eliminar a ${contact.contact_name}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });

      if (result.isConfirmed) {
        setIsSubmitting(true); // Mostrar el loader de acción
        await deleteContact(contact.id, user);
        await loadContacts(selectedCompany);
        setIsSubmitting(false); // Ocultar el loader de acción
        Swal.fire({
          icon: "success",
          title: "Eliminado",
          text: "El contacto ha sido eliminado correctamente",
          timer: 1500
        });
      }
    } catch (error) {
      setIsSubmitting(false);
      console.error("Error deleting contact:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo eliminar el contacto"
      });
    }
  };

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.searchable-select')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  if (loading) return <PageLoader message="Cargando contactos..." />;
  
  // Determinar el título según el modo
  const bannerTitle = isMyContactsMode ? "Mis Contactos" : "Gestión de Contactos";
  const bannerMessage = isMyContactsMode 
    ? "Visualiza y gestiona tus contactos empresariales." 
    : "Visualiza y gestiona los contactos de los socios.";
  
  return (
    <div>
      <ActionLoader open={isSubmitting} onClose={()=>{}} />

      <Banner title={bannerTitle} message={bannerMessage} />

      {/* Input de búsqueda de empresas (solo para admins en modo contactos de socios) */}
      {!isMyContactsMode && user?.role === "admin" && (
        <div className="flex items-center gap-4 mb-6">
          <div className="relative w-full max-w-md searchable-select">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => setShowDropdown(true)}
              placeholder="Buscar empresa por nombre, RUT o nombre comercial..."
              className="w-full border border-gray-300 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0488AF] focus:border-transparent text-black shadow-sm"
            />
            
            {showDropdown && searchTerm && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {filterCompanies(searchTerm).length > 0 ? (
                  filterCompanies(searchTerm).map((company) => (
                    <div
                      key={company.business_name}
                      onClick={() => handleCompanySelect(company)}
                      className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                    >
                      <div className="font-medium text-gray-900">{company.business_name}</div>
                      <div className="flex justify-between items-center mt-1">
                        {company.trade_name && (
                          <div className="text-sm text-gray-600">{company.trade_name}</div>
                        )}
                        {company.rut && (
                          <div className="text-xs text-gray-500 font-mono">{company.rut}</div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-4 py-3 text-gray-500 text-sm">No se encontraron empresas</div>
                )}
              </div>
            )}
            
            {selectedCompany && searchTerm && (
              <button
                onClick={clearSelection}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      )}

      {selectedCompany && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">
                Contactos de: <span className="text-[#0488AF]">{selectedCompany}</span>
              </h3>
              <p className="text-sm text-gray-600">
                {contacts.length} contacto{contacts.length !== 1 ? 's' : ''} encontrado{contacts.length !== 1 ? 's' : ''}
              </p>
            </div>
            <button
              onClick={() => handleOpenModal(null, "create")}
              className="bg-[#1BBB55] text-white px-4 py-2 rounded-lg hover:bg-[#1aab51] flex items-center gap-2 whitespace-nowrap transition-colors shadow-sm"
            >
              <Plus size={16} /> Añadir contacto
            </button>
          </div>
        </div>
      )}

      {/* Tabla de contactos (solo se muestra si hay una empresa seleccionada o estamos en modo "Mis Contactos") */}
      {(selectedCompany || isMyContactsMode) && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nombre
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Correo
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Teléfono
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cargo
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className={`bg-white divide-y divide-gray-200`}>
                {contacts.length > 0 ? (
                  contacts.map((contact) => (
                    <tr
                      key={contact.id}
                      className={`${
                        contact.cobranza === true ? 'bg-yellow-50 hover:bg-yellow-100' : ''
                      } hover:bg-gray-100 cursor-pointer transition-colors`}
                      onClick={() => handleOpenModal(contact, "view")}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{contact.contact_name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{contact.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{contact.phone_number}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{contact.title_job}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(contact);
                          }}
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center">
                      <div className="text-gray-500">
                        <div className="text-lg mb-2">📋</div>
                        <div className="text-sm">No hay contactos disponibles.</div>
                        {selectedCompany && (
                          <div className="text-xs text-gray-400 mt-1">
                            Añade el primer contacto para {selectedCompany}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {!isMyContactsMode && !selectedCompany && (
        <div className="text-center p-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <div className="text-gray-500">
            <div className="text-4xl mb-4">🏢</div>
            <div className="text-lg font-medium mb-2">Selecciona una empresa</div>
            <div className="text-sm">Busca y selecciona una empresa para ver sus contactos.</div>
          </div>
        </div>
      )}

      {/* Modal para ver/editar/crear contacto */}
      <ModalTransition
        isOpen={isModalOpen}
        onClose={() => {}}              //* 👈 NO-OP: evita cierre por clic fuera */
        className="max-w-2xl w-full"
      >
        <ContactDetailsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}   //* 👈 cierre controlado SOLO desde dentro 
          contact={selectedContact}
          companyName={selectedCompany}
          mode={modalMode}
        />
      </ModalTransition>
    </div>
  );
};

export default ContactMembers;
